import {
  OrgTradeExportPanel,
  OrgSettlementExportPanel,
  OrgOrderAuditExportPanel,
} from './OrgCsvExportPanels';
import {
  OrgGoogleSheetLoadPanel,
  OrgGoogleSheetProductionUpsertPanel,
} from './GoogleSheetsPanels';
import type { useExportWorkflows } from './useExportWorkflows';

/**
 * The Manager Exports tab (#343): the three CSV panels, the Sheets test load,
 * and — for admins only — the production upsert.
 *
 * Composition only. Every value comes from useExportWorkflows; this file owns
 * no state and performs no fetch, so the panels below stay renderable in
 * isolation and the workflow stays testable without them.
 */
export function ExportsPanel({
  adminMode,
  ex,
}: {
  adminMode?: boolean;
  ex: ReturnType<typeof useExportWorkflows>;
}) {
  const {
    exportFromDate,
    setExportFromDate,
    exportToDate,
    setExportToDate,
    selectedExportTraderIds,
    setSelectedExportTraderIds,
    googleSpreadsheetId,
    setGoogleSpreadsheetId,
    googleTargetSheetTitle,
    setGoogleTargetSheetTitle,
    googleTraderId,
    setGoogleTraderId,
    googleAllTraders,
    googleFromDate,
    setGoogleFromDate,
    googleToDate,
    setGoogleToDate,
    googleAccessToken,
    setGoogleAccessToken,
    googleSourceLabel,
    setGoogleSourceLabel,
    googleBettorLabel,
    setGoogleBettorLabel,
    googleBookLabel,
    setGoogleBookLabel,
    googleLoading,
    googleMessage,
    googleError,
    googleProdSpreadsheetId,
    setGoogleProdSpreadsheetId,
    googleProdSourceSheetTitle,
    setGoogleProdSourceSheetTitle,
    googleProdTargetSheetTitle,
    setGoogleProdTargetSheetTitle,
    googleProdTraderId,
    setGoogleProdTraderId,
    googleProdFromDate,
    setGoogleProdFromDate,
    googleProdToDate,
    setGoogleProdToDate,
    googleProdVenue,
    setGoogleProdVenue,
    googleProdAllowPolymarket,
    setGoogleProdAllowPolymarket,
    googleProdAccessToken,
    setGoogleProdAccessToken,
    googleProdSourceLabel,
    setGoogleProdSourceLabel,
    googleProdBettorLabel,
    setGoogleProdBettorLabel,
    googleProdBookLabel,
    setGoogleProdBookLabel,
    googleProdPreview,
    googleProdConfirmation,
    setGoogleProdConfirmation,
    googleProdLoading,
    googleProdMessage,
    googleProdError,
    exports,
    exportMembers,
    googleAccounts,
    googleTraderScopeReady,
    handleOrgExport,
    handleOrgSettlementExport,
    handleOrgOrderAuditExport,
    handleExportPreset,
    handleGoogleAllTradersChange,
    handleGoogleSheetLoad,
    handleGoogleProductionPreview,
    handleGoogleProductionApply,
  } = ex;

  return (
    <>
      <div className="mb-6 grid items-stretch gap-4 xl:grid-cols-2">
        <OrgTradeExportPanel
          fromDate={exportFromDate}
          toDate={exportToDate}
          onFromDateChange={setExportFromDate}
          onToDateChange={setExportToDate}
          onPreset={handleExportPreset}
          accounts={exportMembers}
          selectedTraderIds={selectedExportTraderIds}
          onSelectedTraderIdsChange={setSelectedExportTraderIds}
          onExport={handleOrgExport}
          busy={exports.isPending('org')}
          exporting={exports.isPending('org')}
          message={exports.entry('org')?.status === 'done' ? exports.entry('org')?.message ?? null : null}
          exportError={exports.entry('org')?.status === 'error' ? exports.entry('org')?.error ?? null : null}
          className="h-full"
        />
        <OrgSettlementExportPanel
          onExport={handleOrgSettlementExport}
          exporting={exports.isPending('org-settlements')}
          message={
            exports.entry('org-settlements')?.status === 'done'
              ? exports.entry('org-settlements')?.message ?? null
              : null
          }
          exportError={
            exports.entry('org-settlements')?.status === 'error'
              ? exports.entry('org-settlements')?.error ?? null
              : null
          }
          className="h-full"
        />
        <OrgOrderAuditExportPanel
          onExport={handleOrgOrderAuditExport}
          exporting={exports.isPending('org-order-audit')}
          message={
            exports.entry('org-order-audit')?.status === 'done'
              ? exports.entry('org-order-audit')?.message ?? null
              : null
          }
          exportError={
            exports.entry('org-order-audit')?.status === 'error'
              ? exports.entry('org-order-audit')?.error ?? null
              : null
          }
          className="h-full"
        />
        <OrgGoogleSheetLoadPanel
          spreadsheetId={googleSpreadsheetId}
          targetSheetTitle={googleTargetSheetTitle}
          traderId={googleTraderId}
          allTraders={googleAllTraders}
          fromDate={googleFromDate}
          toDate={googleToDate}
          accessToken={googleAccessToken}
          sourceLabel={googleSourceLabel}
          bettorLabel={googleBettorLabel}
          bookLabel={googleBookLabel}
          // #461 — scope-gated: empty until the effective org's members load.
          // The CSV panels above deliberately stay on exportMembers.
          accounts={googleAccounts}
          scopeReady={googleTraderScopeReady}
          busy={googleLoading}
          message={googleMessage}
          error={googleError}
          onSpreadsheetIdChange={setGoogleSpreadsheetId}
          onTargetSheetTitleChange={setGoogleTargetSheetTitle}
          onTraderIdChange={setGoogleTraderId}
          onAllTradersChange={handleGoogleAllTradersChange}
          onFromDateChange={setGoogleFromDate}
          onToDateChange={setGoogleToDate}
          onAccessTokenChange={setGoogleAccessToken}
          onSourceLabelChange={setGoogleSourceLabel}
          onBettorLabelChange={setGoogleBettorLabel}
          onBookLabelChange={setGoogleBookLabel}
          onRun={handleGoogleSheetLoad}
          className="h-full"
        />
      </div>
      {adminMode && (
        <OrgGoogleSheetProductionUpsertPanel
          spreadsheetId={googleProdSpreadsheetId}
          sourceSheetTitle={googleProdSourceSheetTitle}
          targetSheetTitle={googleProdTargetSheetTitle}
          traderId={googleProdTraderId}
          fromDate={googleProdFromDate}
          toDate={googleProdToDate}
          venue={googleProdVenue}
          allowPolymarket={googleProdAllowPolymarket}
          accessToken={googleProdAccessToken}
          sourceLabel={googleProdSourceLabel}
          bettorLabel={googleProdBettorLabel}
          bookLabel={googleProdBookLabel}
          accounts={googleAccounts}
          scopeReady={googleTraderScopeReady}
          preview={googleProdPreview}
          confirmation={googleProdConfirmation}
          busy={googleProdLoading}
          message={googleProdMessage}
          error={googleProdError}
          onSpreadsheetIdChange={setGoogleProdSpreadsheetId}
          onSourceSheetTitleChange={setGoogleProdSourceSheetTitle}
          onTargetSheetTitleChange={setGoogleProdTargetSheetTitle}
          onTraderIdChange={setGoogleProdTraderId}
          onFromDateChange={setGoogleProdFromDate}
          onToDateChange={setGoogleProdToDate}
          onVenueChange={setGoogleProdVenue}
          onAllowPolymarketChange={setGoogleProdAllowPolymarket}
          onAccessTokenChange={setGoogleProdAccessToken}
          onSourceLabelChange={setGoogleProdSourceLabel}
          onBettorLabelChange={setGoogleProdBettorLabel}
          onBookLabelChange={setGoogleProdBookLabel}
          onConfirmationChange={setGoogleProdConfirmation}
          onPreview={handleGoogleProductionPreview}
          onApply={handleGoogleProductionApply}
          className="mb-6"
        />
      )}
    </>
  );
}
